import { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";

import AuthSubHeader from "src/components/authSubHeader";
import FooterText from "src/components/footerText";
import CaseStepOne from "src/components/caseStepOne";
import CaseStepTwo from "src/components/caseStepTwo";
import CaseStepThree from "src/components/caseStepThree";
import RestoreCaseSessionModal from "./modal";
import PendingSubmissions from "src/components/addCase/pendingSubmissions";
import PatientLookupModal from "src/components/addCase/lookupPatientModal";

import { CaseContext } from "src/context/caseContext";
import { AuthContext } from "src/context/authContext";
import { AddCaseSchema } from "src/validation/addCase";
import { ToastContext } from "src/context/toastContext";
import useCaseCreateCall from "src/hooks/useCaseCreateCall";
import { useDraftCaseFunc } from "src/hooks/useDraftCaseFunc";
import usePatientLookupCall from "src/hooks/usePatientLookupCall";

import { ToastTypes } from "src/types";
import { AddCaseProps, AddCaseStorageType } from "./types";
import { usePageTrackingAndStorage } from "src/hooks/usePageTrackingAndStorage";
import { CASE_FORM_DEFAULT_VALUES } from "src/constants/cases";
import useFetch from "../../hooks/useFetch";
import { APIEndpoints } from "../../types/apiTypes";




const AddCase: FC = (): JSX.Element => {
  const [step, changeStep] = useState<number>(1);
  const [isStepTwoMounted, mountingStepTwo] = useState<boolean>(false);
  const [isModalVisible, showHideModal] = useState<boolean>(false);
  const [isStepOneInvalid, setStepOneValidity] = useState<boolean>(false);
  const [isStepTwoInvalid, setStepTwoValidity] = useState<boolean>(false);
  // const [isSubmitClicked, setSubmitClicked] = useState<boolean>(false);
  const [isDuplicateModalVisible, setDuplicateModalVisibility] = useState<boolean>(false)
  const [patientPrevCaseDate, updatePatientPrevCaseDate] = useState<string>("")
  const [selectedDoctorObj, setSelectedDoctorObj] = useState<any | null>(null);
  // const [doctorCheck, setDoctorCheck] = useState<boolean>(false);
  const [pendingSubmissionVal, setPendingSubmissionVal] = useState<AddCaseProps | null>(null);

  const { userData } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const { draftCaseId, updateCaseDraftStatus, updateDraftCaseId } = useContext(CaseContext)
  const { saveDraftLoader, updateDraftLoader, saveDraftCase } = useDraftCaseFunc()
  const navigate = useNavigate()

  const { control, formState, handleSubmit, clearErrors, getValues, watch, trigger, reset, setValue, resetField } = useForm<AddCaseProps>({
    mode: "all",
    resolver: yupResolver(AddCaseSchema),
    defaultValues: {
      ...CASE_FORM_DEFAULT_VALUES,
      user_id: userData ? userData.id : 0,
      creator_name: userData ? userData.name : ""
    }
  });

  usePageTrackingAndStorage(getValues, formState.isDirty, step)

  const { addCaseLoading, isUploading, caseDataSubmission } = useCaseCreateCall(getValues)
  const { lookupForPatientName, totalRefills } = usePatientLookupCall(updatePatientPrevCaseDate, setDuplicateModalVisibility, caseDataSubmission)


  const patientData = watch("patient_attributes");

  useEffect(() => {
    if (patientData && patientData.global_patient_id)
      trigger(["patient_attributes", "insurances_attributes"]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientData]);



  const uploadedDocs = watch("uploaded_documents");
  const paApproved = watch("case_attributes.pa_approved");

  useEffect(() => {
    const { errors } = formState
    if (uploadedDocs && uploadedDocs.length > 0 && (errors && errors.uploaded_documents))
      trigger("uploaded_documents")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedDocs])

  useEffect(() => {
    const { errors } = formState
    if (paApproved && paApproved.length > 0 && (errors && errors.uploaded_documents)) {
      clearErrors("uploaded_documents")
    }

    if (paApproved !== "pa_approved") setValue("case_attributes.pa_approved_date", "")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paApproved])





  const restoringCaseData = () => {
    const data = localStorage.getItem('newCase');
    if (data) {
      const { step, newData, draftCaseId } = JSON.parse(data) as unknown as AddCaseStorageType;

      changeStep(Number(step));
      updateDraftCaseId(Number(draftCaseId))
      if (step >= 2) mountingStepTwo(true);
      reset({ ...formState.defaultValues, ...newData })

      if (step >= 2) {
        trigger(["prescription_attributes","prescription_information_attributes"]);
        if (step > 2)
          trigger(["doctor_attributes", "patient_attributes", "insurances_attributes", "case_attributes", "uploaded_documents"]);
      }
    }
    localStorage.removeItem('newCase');
    showHideModal(false);
  };

  const removeCurrentData = () => {
    localStorage.removeItem('newCase');
    reset();
    changeStep(1);
    mountingStepTwo(false)
    showHideModal(false);
  };

  const resetCaseForm = (caseData?: Partial<AddCaseProps>) => {
    if (caseData) reset(caseData)
    else reset({
      ...CASE_FORM_DEFAULT_VALUES,
      user_id: userData ? userData.id : 0,
      creator_name: userData ? userData.name : "",
      doctor_attributes: {
        global_doctor_id: 0,
        practice_id: 0,
      }
    })
  }

  useEffect(() => {
    const data = localStorage.getItem('newCase');
    if (data) showHideModal(true)
  }, [])





  useEffect(() => {
    const { errors } = formState
    if (Object.keys(errors).length > 0) {
      if (Object.hasOwn(errors, 'prescription_attributes') || Object.hasOwn(errors, 'prescription_information_attributes')) {
        setStepOneValidity(true);
      } else {
        setStepOneValidity(false);
      }

      if (Object.hasOwn(errors, 'patient_attributes') ||
        Object.hasOwn(errors, 'case_doctor') ||
        Object.hasOwn(errors, 'insurances_attributes') ||
        Object.hasOwn(errors, "uploaded_documents") ||
        Object.hasOwn(errors, "case_attributes")
      ) {
        setStepTwoValidity(true);
      } else {
        setStepTwoValidity(false);
      }
    } else {
      setStepOneValidity(false);
      setStepTwoValidity(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState]);

  useEffect(() => {
    const messages = [];
    const { errors } = formState

    if (isStepOneInvalid && isStepTwoInvalid && step >= 2) {
      messages.push(`The following sections from step 1 have errors:
      ${(errors.prescription_attributes || errors.prescription_information_attributes) ? '- case prescription<br>' : ''}
      ${errors.case_attributes ? '- case site of care<br>' : ''}`);

      messages.push(`The following sections from step 2 have errors: <br>
      ${errors.doctor_attributes ? '- case doctor<br>' : ''}
      ${errors && errors.patient_attributes ? '- case patient<br>' : ''}
      ${errors.patient_attributes && errors.patient_attributes.hasOwnProperty("phone_numbers") ? '- case patient phone numbers<br>' : ''}
      ${errors.patient_attributes && errors.patient_attributes.address ? '- case patient address<br>' : ''}
      ${errors.insurances_attributes ? '- case patient insurances' : ''}
      ${errors.uploaded_documents ? '- case documents<br>' : ''}`);
    }
    else if (isStepOneInvalid && step >= 1) {
      messages.push(`The following sections from step 1 have errors:
      ${(errors.prescription_attributes || errors.prescription_information_attributes) ? '- case prescription<br>' : ''}
      ${errors.case_attributes ? '- case site of care<br>' : ''}`);
    }
    else if (isStepTwoInvalid && step >= 2) {
      messages.push(`The following sections from step 2 have errors: <br>
      ${errors.doctor_attributes ? '- case doctor<br>' : ''}
      ${errors && errors.patient_attributes ? '- case patient<br>' : ''}
      ${errors.patient_attributes && errors.patient_attributes.hasOwnProperty("phone_numbers") ? '- case patient phone numbers<br>' : ''}
      ${errors.patient_attributes && errors.patient_attributes.address ? '- case patient address<br>' : ''}
      ${errors.insurances_attributes ? '- case patient insurances' : ''}
      ${errors.uploaded_documents ? '- case documents<br>' : ''}
      ${errors.case_attributes ? '- case document options<br>' : ''}`);
    }

    showToast(messages, ToastTypes.WARNING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStepOneInvalid, isStepTwoInvalid, step]);


  const onStepProgressionClick = (localStep: number) => {
    if (step === 1)
      trigger(["prescription_attributes","prescription_information_attributes"]);
    else if (step === 2)
      trigger(["doctor_attributes", "patient_attributes", "insurances_attributes", "uploaded_documents", "case_attributes"]);

    if (localStep >= 2)
      mountingStepTwo(true);
    else if (localStep === 3)
      trigger(["prescription_attributes", "case_attributes", "doctor_attributes", "patient_attributes", "insurances_attributes", "uploaded_documents"]);

    changeStep(localStep);

    //:- logic to save case data in localstorage
    // localStorage.setItem('newCase', JSON.stringify({ newData: getValues(), step }));
  };

  const { data: doctorSignaturesData, error: doctorSignaturesError, mutate: doctorSignaturesMutate }
    = useFetch<any>(APIEndpoints.CHECK_SIGNATURE, {}, {}, false);

  const submitCase = (val: AddCaseProps) => {
    const { firstname, middlename, lastname } = val.patient_attributes;
    lookupForPatientName(firstname, lastname, middlename);
    updateCaseDraftStatus(true);
  };


  useEffect(() => {
    if (doctorSignaturesData && pendingSubmissionVal) {
      submitCase(pendingSubmissionVal);
      setPendingSubmissionVal(null); 
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorSignaturesData, doctorSignaturesError]);

  const onCaseSubmission = (val: AddCaseProps) => {
    if (!selectedDoctorObj) {
      // setSubmitClicked(true);
      showToast(["Prescriber not selected"], ToastTypes.ERROR);
      return;
    }setPendingSubmissionVal(val);
    doctorSignaturesMutate({}, { id: selectedDoctorObj.id });
  };






  const saveDraftAndClearDocument = () => {
    setValue("uploaded_documents",[])
    saveDraftCase(getValues())
  }



  return (
    <div className="d-flex flex-fill overflow-y-auto overflow-x-hidden">
      <div className="container d-flex flex-column" style={{ maxWidth: '90rem' }}>
        <AuthSubHeader />
        <div className="row g-3 prescription-form">
          <div className="col-lg-2 col-12 pendings-form">
            <div className="d-none d-lg-block" style={{ minHeight: '52px' }}></div>
            <PendingSubmissions reset={resetCaseForm} />
          </div>
          <form className="col-lg-10 col-12 d-flex flex-column flex-fill start-form" onSubmit={handleSubmit( onCaseSubmission )}>
            <div className="sticky-top pb-3">
              <div id="case-creation-progress-bar" className="case-progress-bar">
                <button
                  type="button" id="progress-step-1"
                  className={`btn case-creation-progress-step ${step >= 1 ? isStepOneInvalid ? 'invalid-progress-step' : 'active-progress-step' : ''}`}
                  onClick={() => onStepProgressionClick( 1 )}
                >
                  1. Getting Started <i className={`bi bi-play-fill progression-arrow-head`}></i>
                </button>

                <button
                  type="button" id="progress-step-2"
                  className={`btn case-creation-progress-step ${step >= 2 ? isStepTwoInvalid ? 'invalid-progress-step' : 'active-progress-step' : ''}`}
                  onClick={() => onStepProgressionClick( 2 )}
                >
                  2. Patient Information <i className={`bi bi-play-fill progression-arrow-head`}></i>
                </button>

                <button
                  type="button" id="progress-step-3"
                  className={`btn case-creation-progress-step ${step >= 3 ? 'active-progress-step' : ''}`}
                  onClick={() => onStepProgressionClick( 3 )}
                >
                  3. Finish
                </button>
              </div>
            </div>

            {userData ? (
              <>
                <Controller
                  name="creator_name"
                  control={control}
                  defaultValue={userData.name}
                  render={() => ( <></> )}
                />
                <Controller
                  name="user_id"
                  control={control}
                  defaultValue={userData.id}
                  render={() => ( <></> )}
                />
              </>
            ) : ( <></> )}

            <div className="d-flex flex-column flex-fill">
              <div id="step-1" className="case-creation-step">
                {step >= 1 &&
                  <CaseStepOne
                    control={control} watch={watch}
                    setValue={setValue} getValues={getValues}
                    isVisible={step === 1}
                    resetField={resetField}
                  />
                }
              </div>

              <div id="step-2" className="case-creation-step">
                {isStepTwoMounted &&
                  <CaseStepTwo
                    resetField={resetField} control={control}
                    getValues={getValues} watch={watch}
                    setValue={setValue} isVisible={step === 2} trigger={trigger}
                    reset={reset}
                    setSelectedDoctorObj={setSelectedDoctorObj}
                    isCloneCase={false}
                  />
                }
              </div>

              <div id="step-3" className="case-creation-step">
                {step === 3 && <CaseStepThree
                    getValues={getValues}
                    control={control}
                    selectedDoctorObj={selectedDoctorObj}
                />}
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mt-2 sticky-bottom bg-white py-2 px-3 p-md-3 border rounded-2" style={{ boxShadow: '0 0 15px #CCC' }}>
              <button type="button" key="previousStepButton"
                className={`btn btn-outline-secondary ${step === 1 ? 'd-none' : ''}`}
                onClick={() => onStepProgressionClick( step - 1 )}
              ><i className="bi bi-chevron-double-left"></i> Previous
              </button>

              {step === 3 ? (
                <>
                  <button className="btn btn-primary"
                    disabled={
                      (!( patientData.firstname?.length > 0 && patientData.lastname?.length > 0 ))
                      || saveDraftLoader
                      || ( draftCaseId !== 0 && updateDraftLoader )
                    }
                    type="button" onClick={() => saveDraftAndClearDocument()}
                  >
                    {saveDraftLoader || ( draftCaseId !== 0 && updateDraftLoader ) ? (
                      <i className="text-white me-2 spinner-border" style={{ width: `24px`, height: `24px`, fontSize: "10px" }}></i>
                    ) : (
                      <i className="bi bi-files"></i>
                    )}
                    {' '}
                    {draftCaseId === 0 ? "Save as Draft" : "Update Draft"}
                  </button>
                  <button type="submit" disabled={addCaseLoading || isUploading } key="formSubmitButton"
                    className="btn btn-primary">
                    {addCaseLoading || isUploading ? (
                      <i className="text-white me-2 spinner-border" style={{ width: `24px`, height: `24px`, fontSize: "10px" }}></i>
                    ) : null} <i className="bi bi-send-fill me-2"></i>
                    Submit Request
                  </button></>
              ) : (
                <button type="button" key="nextStepButton" className="btn btn-outline-secondary"
                  onClick={() => onStepProgressionClick( step + 1 )}>
                  Next <i className="bi bi-chevron-double-right"></i>
                </button>
              )}

              {step !== 3 && (
                <button className="btn btn-primary"
                  disabled={
                    ( !( patientData.firstname?.length > 0 && patientData.lastname?.length > 0 ) )
                    || saveDraftLoader
                    || ( draftCaseId !== 0 && updateDraftLoader )
                  }
                  type="button"
                  onClick={() => saveDraftAndClearDocument()}
                >
                  {saveDraftLoader || ( draftCaseId !== 0 && updateDraftLoader ) ? (
                    <i className="text-white me-2 spinner-border" style={{ width: `24px`, height: `24px`, fontSize: "10px" }}></i>
                  ) : (
                    <i className="bi bi-files"></i>
                  )}
                  {' '}
                  {draftCaseId === 0 ? "Save as Draft" : "Update Draft"}
                </button>
              )}
            </div>
          </form>
        </div>

        <FooterText footerType="black" />
      </div>

      <RestoreCaseSessionModal
        isModalVisible={isModalVisible} removeCurrentData={removeCurrentData}
        restoringCaseData={restoringCaseData}
      />

      <PatientLookupModal
        isModalVisible={isDuplicateModalVisible}
        patientPrevCaseDate={patientPrevCaseDate}
        totalRefills={totalRefills}
        caseDataSubmission={() => {
          updateCaseDraftStatus(true)
          setDuplicateModalVisibility(false)
          caseDataSubmission()
        }}
        clearCaseData={() => {
          setDuplicateModalVisibility(false)
          navigate("/cases")
        }}
      />
    </div>
  );
};

export default AddCase;
